import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  pointsApi,
  generateIdempotencyKey,
  type ApprovePointsEntryRequest,
  type CreatePointsAdjustmentRequest,
  type RedeemPointsRequest,
  type RejectPointsEntryRequest,
  type ReversePointsEntryRequest,
} from "../services/pointsApi";

const accountKey = (customerId: string) => ["points", "customer", customerId] as const;
const statementKey = (customerId: string) => ["points", "statement", customerId] as const;
const pendingKey = ["points", "pending"] as const;

// ── Reads ───────────────────────────────────────────────────────────────────

export const usePointsByCustomer = (customerId: string | undefined) =>
  useQuery({
    queryKey: customerId ? accountKey(customerId) : ["points", "customer", "none"],
    queryFn: () => pointsApi.getByCustomer(customerId!),
    enabled: !!customerId,
    staleTime: 0,
    refetchOnMount: "always",
  });

export const usePointsStatement = (
  customerId: string | undefined,
  params?: { from?: string; to?: string; pageNumber?: number; pageSize?: number }
) =>
  useQuery({
    queryKey: customerId ? [...statementKey(customerId), params] : ["points", "statement", "none"],
    queryFn: () => pointsApi.getStatement(customerId!, params),
    enabled: !!customerId,
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useRedeemablePreview = (customerId: string | undefined) =>
  useQuery({
    queryKey: ["points", "redeemable", customerId],
    queryFn: () => pointsApi.getRedeemable(customerId!),
    enabled: !!customerId,
    staleTime: 0,
  });

export const useEarningPreview = (
  customerId: string | undefined,
  params: { orderAmount: number; orderType?: string | null; productCategories?: string[] }
) => {
  const categoriesKey = [...(params.productCategories ?? [])].sort().join(",");
  return useQuery({
    queryKey: [
      "points",
      "earning-preview",
      customerId,
      Math.round(params.orderAmount),
      params.orderType ?? "",
      categoriesKey,
    ],
    queryFn: () => pointsApi.getEarningPreview(customerId!, params),
    enabled: !!customerId && params.orderAmount > 0,
    staleTime: 0,
  });
};

export const usePendingPointsApprovals = (params?: {
  type?: string;
  customer?: string;
  pageNumber?: number;
  pageSize?: number;
}) =>
  useQuery({
    queryKey: [...pendingKey, params],
    queryFn: () => pointsApi.getPending(params),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });

// ── Writes ──────────────────────────────────────────────────────────────────

const invalidateAll = () => {
  queryClient.invalidateQueries({ queryKey: pendingKey });
  queryClient.invalidateQueries({ queryKey: ["points"] });
};

export const useCreatePointsAdjustment = () =>
  useMutation({
    mutationFn: (payload: CreatePointsAdjustmentRequest) =>
      pointsApi.createAdjustment(payload, generateIdempotencyKey()),
    onSuccess: invalidateAll,
  });

export const useRedeemPoints = () =>
  useMutation({
    mutationFn: (payload: RedeemPointsRequest) => pointsApi.redeem(payload, generateIdempotencyKey()),
    onSuccess: invalidateAll,
  });

export const useApprovePointsEntry = () =>
  useMutation({
    mutationFn: ({ entryId, payload }: { entryId: string; payload: ApprovePointsEntryRequest }) =>
      pointsApi.approve(entryId, payload, generateIdempotencyKey()),
    onSuccess: invalidateAll,
  });

export const useRejectPointsEntry = () =>
  useMutation({
    mutationFn: ({ entryId, payload }: { entryId: string; payload: RejectPointsEntryRequest }) =>
      pointsApi.reject(entryId, payload, generateIdempotencyKey()),
    onSuccess: invalidateAll,
  });

export const useReversePointsEntry = () =>
  useMutation({
    mutationFn: ({ entryId, payload }: { entryId: string; payload: ReversePointsEntryRequest }) =>
      pointsApi.reverse(entryId, payload, generateIdempotencyKey()),
    onSuccess: invalidateAll,
  });
